import CategoryPill from '@/components/atoms/category-pill';
import { classnames } from '@/utils/classnames/classnames';
import { useTranslations } from 'use-intl';
import type { SubcategoryLink } from '@/lib/tastics/product-list/components/product-list-view-model/helpers/subcategoryNav';

interface SubcategoryNavProps {
  subcategories: SubcategoryLink[];
  className?: string;
}

// EWM-832 — subcategory navigation (phase 1: no imagery, no banner).
//
// Every qualifying child is listed — the row wraps onto as many lines as it
// needs, with no truncation, no "show more" and no cap, per AC.
//
// Each child renders as the shared `atoms/category-pill`, i.e. the same chip
// the CategoryCardsGrid homepage cards use (AC-12/AC-15) — one definition, so
// the two surfaces cannot drift. Phase 2 icons are EWM-974; nothing here
// renders imagery.
//
// The bottom rule is owned by this component rather than by the PLP's filter
// row (AC-13/AC-14): that row is shared with the search and collections pages,
// so a `border-t` there would draw a stray rule on every page that has no
// subcategories. The caller renders this ONLY when there is at least one child
// to show, so the rule appears exactly where the pills do — and there is no
// empty-state branch here, because an always-rendered wrapper would leave a
// gap (and now a stray line) between the heading and the filter row.
//
// The rule is `border-gray-300`, NOT the `border-gray-200` its neighbours use.
// Every PLP sits on the `bg-gray-100` page ground (`app/[locale]/layout.tsx:269`)
// = rgb(252,252,253); `gray-200` = rgb(247,247,247) is a 5/255 delta against it
// (~1.05:1) — measured invisible on the real surface, so it would not deliver
// the separation AC-13 asks for. `gray-300` = rgb(209,209,209) is ~1.49:1 and is
// the same value the pill's own border already uses, so the block reads as one
// system. The filter row below still uses `gray-200`; that row is shared with
// search and collections and is out of scope here (logged as a follow-up).
const SubcategoryNav = ({ subcategories, className }: SubcategoryNavProps) => {
  const translate = useTranslations();

  return (
    <nav
      aria-label={translate('product.subcategory-nav')}
      className={classnames('mb-6 border-b border-gray-300 pb-4', className)}
    >
      {/* `gap-2` matches the CategoryCardsGrid chip list — the pill's own
          px-3/py-1.5 supplies the breathing room the old text links needed
          `gap-x-6` for. */}
      <ul className="flex flex-wrap items-center gap-2">
        {subcategories.map((subcategory) => (
          <li key={subcategory.href}>
            <CategoryPill label={subcategory.label} href={subcategory.href} />
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default SubcategoryNav;
